/* FrameDex — global.js
   Small shared helpers + scroll reveal + off-canvas menu + focus utilities.
   Loaded with `defer`. Everything degrades gracefully without JS. */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const animationsOn = document.documentElement.dataset.animations !== 'false';

  /* ---- Scroll reveal ---------------------------------------------------- */
  function initReveal() {
    const els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;
    if (reduceMotion || !animationsOn || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    els.forEach((el) => io.observe(el));
  }

  /* ---- Focus trap helper (shared by drawers/menus) ---------------------- */
  const FOCUSABLE =
    'a[href], button:not([disabled]), textarea, input, select, details, [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    const focusables = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
  window.FrameDex = window.FrameDex || {};
  window.FrameDex.trapFocus = trapFocus;
  window.FrameDex.FOCUSABLE = FOCUSABLE;

  /* ---- Off-canvas mobile menu ------------------------------------------- */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('[data-menu-panel]');
      this.scrim = this.querySelector('[data-menu-scrim]');
      this.openBtn = document.querySelector('[data-menu-open]');
      this.closeBtn = this.querySelector('[data-menu-close]');
      this.lastFocus = null;

      if (this.openBtn) this.openBtn.addEventListener('click', () => this.open());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.scrim) this.scrim.addEventListener('click', () => this.close());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab' && this.hasAttribute('open')) trapFocus(this.drawer, e);
      });

      // Submenu toggles inside the drawer — the whole row is the button; the
      // submenu <ul> is its next sibling.
      this.querySelectorAll('[data-submenu-toggle]').forEach((btn) => {
        const panel = btn.nextElementSibling;
        btn.addEventListener('click', () => {
          const expanded = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!expanded));
          if (panel) panel.hidden = expanded;
        });
      });
    }

    open() {
      this.lastFocus = document.activeElement;
      this.setAttribute('open', '');
      document.body.classList.add('drawer-open');
      if (this.openBtn) this.openBtn.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        if (this.closeBtn) this.closeBtn.focus();
      });
    }

    close() {
      this.removeAttribute('open');
      document.body.classList.remove('drawer-open');
      if (this.openBtn) {
        this.openBtn.setAttribute('aria-expanded', 'false');
        this.openBtn.focus();
      } else if (this.lastFocus) {
        this.lastFocus.focus();
      }
    }
  }
  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);

  /* ---- Sticky header shadow on scroll ----------------------------------- */
  function initHeaderScroll() {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  /* ---- Recently viewed products (local, privacy-friendly) --------------- */
  function trackRecentlyViewed() {
    const el = document.querySelector('[data-recently-viewed-track]');
    if (!el) return;
    const handle = el.dataset.recentlyViewedTrack;
    try {
      const key = 'framedex:recently-viewed';
      let list = JSON.parse(localStorage.getItem(key) || '[]');
      list = list.filter((h) => h !== handle);
      list.unshift(handle);
      list = list.slice(0, 8);
      localStorage.setItem(key, JSON.stringify(list));
    } catch (e) {
      /* storage unavailable — no-op */
    }
  }

  /* ---- Boot ------------------------------------------------------------- */
  function boot() {
    initReveal();
    initHeaderScroll();
    trackRecentlyViewed();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Re-run reveal when sections are re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', initReveal);
})();
